import bridge from '@vkontakte/vk-bridge';

/** VK `vk_platform` values that mean a desktop / PC host. */
const VK_DESKTOP_PLATFORMS = new Set([
  'desktop_web',
  'desktop_app_messenger',
  'desktop_web_messenger',
  'web_external',
]);

/**
 * VK launch params from the current URL (search preferred, then hash with vk_app_id).
 */
export function getVkLaunchParamsFromLocation(
  location: Pick<Location, 'search' | 'hash'> = window.location,
): URLSearchParams {
  const fromSearch = new URLSearchParams(location.search);
  if (fromSearch.has('vk_app_id')) {
    return fromSearch;
  }
  const hash = location.hash.replace(/^#/, '');
  if (!hash) {
    return fromSearch;
  }
  const fromHash = new URLSearchParams(hash);
  return fromHash.has('vk_app_id') ? fromHash : fromSearch;
}

/** True when the mini app was opened from a desktop VK / browser host. */
export function isVkDesktopPlatform(
  params: URLSearchParams = getVkLaunchParamsFromLocation(),
): boolean {
  const platform = params.get('vk_platform');
  return platform != null && VK_DESKTOP_PLATFORMS.has(platform);
}

/** `vk_are_notifications_enabled === 1` in launch params. */
export function areNotificationsEnabledFromLaunchParams(
  params: URLSearchParams = getVkLaunchParamsFromLocation(),
): boolean {
  return params.get('vk_are_notifications_enabled') === '1';
}

/**
 * Fresh launch params from the host (falls back to URL on failure / non-VK).
 */
export async function fetchVkLaunchParams(): Promise<URLSearchParams> {
  try {
    const data = await bridge.send('VKWebAppGetLaunchParams');
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(data)) {
      if (value != null) {
        params.set(key, String(value));
      }
    }
    return params;
  } catch {
    return getVkLaunchParamsFromLocation();
  }
}

export async function areNotificationsEnabled(): Promise<boolean> {
  const params = await fetchVkLaunchParams();
  return areNotificationsEnabledFromLaunchParams(params);
}
